import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const DisclaimerPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="gradient-hero py-12 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4" variant="outline">
              Legal
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">Disclaimer</h1>
            <p className="text-muted-foreground">Last updated: January 2025</p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            <Alert variant="default" className="border-primary/50 bg-primary/5">
              <AlertCircle className="h-5 w-5 text-primary" />
              <AlertTitle className="text-primary">Important Notice</AlertTitle>
              <AlertDescription className="text-muted-foreground">
                The information and calculators provided on wealthcalc are for educational and informational purposes
                only. They should not be considered as professional financial, investment, tax, or legal advice.
              </AlertDescription>
            </Alert>

            <Card>
              <CardContent className="p-8 md:p-12 prose prose-slate max-w-none">
                <div className="space-y-8">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">General Disclaimer</h2>
                    <p className="text-muted-foreground">
                      wealthcalc ("we," "our," or "us") provides financial calculators and tools as a convenience to
                      users. While we strive for accuracy, we make no representations or warranties regarding the
                      completeness, accuracy, or reliability of any calculations, content, or information provided
                      through our Service.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Not Financial Advice</h2>
                    <p className="text-muted-foreground mb-3">
                      The calculators and information on this website are not intended to provide:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2">
                      <li>Financial planning or investment advice</li>
                      <li>Tax advice or tax preparation services</li>
                      <li>Legal advice or legal services</li>
                      <li>Accounting or bookkeeping services</li>
                      <li>Professional consultation of any kind</li>
                    </ul>
                    <p className="text-muted-foreground mt-3">
                      Always consult with qualified professionals before making financial decisions.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Accuracy of Calculations</h2>
                    <p className="text-muted-foreground">
                      While we use industry-standard formulas and algorithms, calculation results may vary based on:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-3">
                      <li>Input data accuracy</li>
                      <li>Rounding methods</li>
                      <li>Browser limitations</li>
                      <li>Formula simplifications</li>
                      <li>Market conditions and variables not accounted for</li>
                    </ul>
                    <p className="text-muted-foreground mt-3">
                      Results should be verified independently before use in actual financial decisions.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Investment Risks</h2>
                    <p className="text-muted-foreground">
                      Investment calculators provided on this site show hypothetical returns and do not guarantee future
                      results. All investments carry risk, including potential loss of principal. Past performance does
                      not guarantee future results.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Tax Information</h2>
                    <p className="text-muted-foreground">
                      Tax calculators are based on general tax rules and may not account for:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-3">
                      <li>Recent tax law changes</li>
                      <li>State and local tax variations</li>
                      <li>Individual circumstances</li>
                      <li>Special deductions or credits</li>
                    </ul>
                    <p className="text-muted-foreground mt-3">
                      Always consult with a qualified tax professional for personalized advice.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Loan Calculations</h2>
                    <p className="text-muted-foreground">
                      Loan calculators provide estimates based on the information you provide. Actual loan terms,
                      interest rates, fees, and payments may differ based on lender requirements, credit scores, and
                      other factors. Always verify with your lender.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">No Liability</h2>
                    <p className="text-muted-foreground">
                      We shall not be liable for any damages, losses, or consequences arising from:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-3">
                      <li>Use or reliance on our calculators or information</li>
                      <li>Errors or inaccuracies in calculations</li>
                      <li>Financial decisions made based on our tools</li>
                      <li>Technical issues or service interruptions</li>
                      <li>Third-party content or links</li>
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Regional Variations</h2>
                    <p className="text-muted-foreground">
                      Financial rules, regulations, and best practices vary by country, state, and region. Our tools
                      may not account for all regional variations. Verify applicability to your specific location.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Updates and Changes</h2>
                    <p className="text-muted-foreground">
                      Financial laws, tax codes, and calculation methodologies change regularly. While we attempt to
                      keep our tools updated, there may be delays. This disclaimer may be updated without notice.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Recommendation</h2>
                    <p className="text-muted-foreground">
                      We strongly recommend consulting with qualified financial advisors, tax professionals, attorneys,
                      or other licensed professionals before making important financial decisions. Use our tools as a
                      starting point for research, not as a substitute for professional advice.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-2xl font-display font-bold text-foreground mb-4">Contact Us</h2>
                    <p className="text-muted-foreground">
                      If you have questions about this disclaimer, please contact us at:
                      <br />
                      <strong>Email:</strong> legal@wealthcalc.online
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DisclaimerPage;
